import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'

import { createBrowserRouter, RouterProvider, ScrollRestoration, Outlet } from 'react-router-dom'

import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Collaborations from './pages/Collaborations'
import Help from './pages/Help'
import CollaborationDetail from './pages/CollaborationDetail'
import SubmitCreativeConcept from './pages/SubmitCreativeConcept'
import UploadVideoReview from './pages/UploadVideoReview'
import UploadFinalVideo from './pages/UploadFinalVideo'
import CollaborationLive from './pages/CollaborationLive'
import OfferDetail from './pages/OfferDetail'
import Performance from './pages/Performance'
import Payments from './pages/Payments'
import InvoiceReview from './pages/InvoiceReview'
import StripeReturn from './pages/StripeReturn'
import StripeRefresh from './pages/StripeRefresh'
import PendingContent from './pages/PendingContent'
import ApprovedContent from './pages/ApprovedContent'
import FinalVideo from './pages/FinalVideo'
import ScheduledPost from './pages/ScheduledPost'
import Settings from './pages/Settings'
import PublicProfilePage from './pages/PublicProfilePage'
import InstagramConnect from './pages/InstagramConnect'
import InstagramCallback from './pages/InstagramCallback'
import YouTubeConnect from './pages/YouTubeConnect'
import YouTubeCallback from './pages/YouTubeCallback'
import YouTubeAuthSteps from './pages/YouTubeAuthSteps'
import TikTokConnect from './pages/TikTokConnect'
import TikTokCallback from './pages/TikTokCallback'
import TikTokAuthSteps from './pages/TikTokAuthSteps'
import NotFound from './pages/NotFound'
import SignIn from './pages/SignIn'
import Onboarding from './pages/Onboarding'
import VerifyEmail from './pages/VerifyEmail'
import EmailConfirmation from './pages/EmailConfirmation'
import ForgotPassword from './pages/ForgotPassword'
import SignInWithEmail from './pages/SignInWithEmail'
import VerifyMagicLink from './pages/VerifyMagicLink'
import ResetPassword from './pages/ResetPassword'
import TermsAndConditions from './pages/TermsAndConditions'
import VideoInfo from './pages/VideoInfo'
import PublicProfileView from './pages/PublicProfileView'
import LicensingPage from './pages/LicensingPage'
import AcceptManagerInvite from './pages/AcceptManagerInvite'
import AcceptMarketerInvite from './pages/AcceptMarketerInvite'
import ClaimAccount from './pages/ClaimAccount'
import Messages from './pages/Messages'
import WelcomeWalkthrough from './pages/WelcomeWalkthrough'
import ComingSoonPage from './pages/ComingSoonPage'
import CalendarView from './pages/CalendarView'
import CalendarSetup from './pages/CalendarSetup'
import GoogleCalendarConnect from './pages/GoogleCalendarConnect'
import GoogleCalendarCallback from './pages/GoogleCalendarCallback'
import OutlookCalendarConnect from './pages/OutlookCalendarConnect'
import OutlookCalendarCallback from './pages/OutlookCalendarCallback'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary, { ErrorFallback } from './components/ErrorBoundary'
import { store } from './store'
import { Provider } from 'react-redux'
import { CampaignsProvider } from './contexts/CampaignsContext'

function RootLayout() {
  return (
    <CampaignsProvider>
      <ScrollRestoration />
      <Outlet />
    </CampaignsProvider>
  )
}

const basename = (import.meta.env.VITE_BASE ?? '/').replace(/\/$/, '')

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      errorElement: <ErrorFallback />,
      children: [
        { path: '/signin', element: <SignIn /> },
        { path: '/signin-email', element: <SignInWithEmail /> },
        { path: '/verify-magic-link/:token', element: <VerifyMagicLink /> },
        { path: '/onboarding', element: <Onboarding /> },
        { path: '/verify-email', element: <VerifyEmail /> },
        { path: '/confirm-email', element: <EmailConfirmation /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/terms', element: <TermsAndConditions /> },
        { path: '/how-to-earn', element: <VideoInfo /> },
        { path: '/p/:username', element: <PublicProfileView /> },
        { path: '/manager-invite/:token', element: <AcceptManagerInvite /> },
        { path: '/marketer-invite/:token', element: <AcceptMarketerInvite /> },
        { path: '/claim-account/:token', element: <ClaimAccount /> },
        {
          element: <ProtectedRoute />,
          children: [
            {
              element: <Layout />,
              children: [
                { path: '/', element: <Dashboard /> },
                { path: 'collaborations', element: <Collaborations /> },
                { path: 'campaigns', element: <Collaborations /> },
                { path: 'messages', element: <Messages /> },
                { path: 'messages/:id', element: <Messages /> },
                { path: 'bids/:id', element: <OfferDetail /> },
                { path: 'collaborations/:id', element: <CollaborationDetail /> },
                { path: 'campaigns/:id', element: <CollaborationDetail /> },
                { path: 'collaborations/:id/concept', element: <SubmitCreativeConcept /> },
                { path: 'campaigns/:id/concept', element: <SubmitCreativeConcept /> },
                { path: 'collaborations/:id/upload-review', element: <UploadVideoReview /> },
                { path: 'campaigns/:id/upload-review', element: <UploadVideoReview /> },
                { path: 'collaborations/:id/upload-final', element: <UploadFinalVideo /> },
                { path: 'campaigns/:id/upload-final', element: <UploadFinalVideo /> },
                { path: 'collaborations/:id/collaboration-live', element: <CollaborationLive /> },
                { path: 'campaigns/:id/collaboration-live', element: <CollaborationLive /> },
                { path: 'collaborations/:id/licensing', element: <LicensingPage /> },
                { path: 'campaigns/:id/licensing', element: <LicensingPage /> },
                { path: 'collaborations/:id/final', element: <FinalVideo /> },
                { path: 'campaigns/:id/final', element: <FinalVideo /> },
                { path: 'collaborations/:id/invoice', element: <InvoiceReview /> },
                { path: 'campaigns/:id/invoice', element: <InvoiceReview /> },
                { path: 'scheduling/:id', element: <ScheduledPost /> },
                { path: 'performance', element: <Performance /> },
                { path: 'public-profile', element: <PublicProfilePage /> },
                { path: 'earnings', element: <Payments /> },
                { path: 'earnings/stripe-return', element: <StripeReturn /> },
                { path: 'earnings/stripe-refresh', element: <StripeRefresh /> },
                { path: 'pending', element: <PendingContent /> },
                { path: 'approved', element: <ApprovedContent /> },
                { path: 'settings', element: <Settings /> },
                { path: 'welcome', element: <WelcomeWalkthrough /> },
                { path: 'help', element: <Help /> },
                { path: 'tools/caption-writer', element: <ComingSoonPage /> },
                { path: 'tools/content-ideas', element: <ComingSoonPage /> },
                { path: 'tools/best-time', element: <ComingSoonPage /> },
                { path: 'tools/schedule-post', element: <ComingSoonPage /> },
                { path: 'connect/instagram', element: <InstagramConnect /> },
                { path: 'instagram/callback', element: <InstagramCallback /> },
                { path: 'connect/youtube', element: <YouTubeConnect /> },
                { path: 'youtube/callback', element: <YouTubeCallback /> },
                { path: 'youtube/auth-steps', element: <YouTubeAuthSteps /> },
                { path: 'connect/tiktok', element: <TikTokConnect /> },
                { path: 'tiktok/callback', element: <TikTokCallback /> },
                { path: 'tiktok/auth-steps', element: <TikTokAuthSteps /> },
                { path: 'calendar', element: <CalendarView /> },
                { path: 'calendar-setup', element: <CalendarSetup /> },
                { path: 'connect/google-calendar', element: <GoogleCalendarConnect /> },
                { path: 'gcal/callback', element: <GoogleCalendarCallback /> },
                { path: 'connect/outlook-calendar', element: <OutlookCalendarConnect /> },
                { path: 'outlook/callback', element: <OutlookCalendarCallback /> },
                { path: '*', element: <NotFound /> },
              ],
            },
          ],
        },
      ],
    },
  ],
  { basename },
)

const App = () => (
  <Provider store={store}>
    <TooltipProvider>
      <Sonner />
      <RouterProvider router={router} />
    </TooltipProvider>
  </Provider>
)

export default App
